import { QueryClient } from '@tanstack/react-query';
import {
	type AsyncStorage,
	type PersistedQuery,
	experimental_createQueryPersister,
} from '@tanstack/react-query-persist-client';
import { get, set, del, entries, createStore, type UseStore } from 'idb-keyval';
import config from '@/config';

const store = createStore('canvasflow_db', 'react-query-cache');
const storage = idbStorage(store);

export async function clearPersistStorage() {
	if (!storage.entries) {
		return;
	}

	for (const [key] of await storage.entries()) {
		await storage.removeItem(key);
	}
}

// The key the previous persister (createAsyncStoragePersister) used to
// store the *entire* dehydrated query cache as one JSON blob under a
// single IndexedDB entry. Superseded by queryPersister below, which
// stores one entry per query keyed by its own query hash instead --
// removed on boot (see bootQueryPersistence) so it doesn't linger as
// dead weight for returning users who last visited before this change.
const LEGACY_QUERY_CACHE_KEY = 'cf-query';

// In-memory retention (gcTime) and persisted-storage retention (maxAge)
// both follow the same online/offline split, per explicit product decision:
// this app is mostly "read a magazine issue" -- if the user leaves an issue
// and comes back within a couple hours while online, keep it around (both
// in memory and in IndexedDB); past that, they're unlikely to return to
// that exact issue, so let it go rather than hold it for no benefit (a
// fresh fetch on next visit is cheap while online). Offline, there's no
// "next fetch" to fall back on, so keep data resident for a full day
// instead of evicting it out from under a reader who's still using the
// app, just not currently looking at that screen.
export const CACHE_DURATION_ONLINE = 1000 * 60 * 60 * 2; // 2 hours
export const CACHE_DURATION_OFFLINE = 1000 * 60 * 60 * 24; // 24 hours

// Default freshness window while online, used by getQueryCacheOptions below.
// staleTime while offline is deliberately Infinity, not this -- offline,
// there's nothing to refetch from anyway (see getQueryCacheOptions), so
// treating cached data as eternally fresh just avoids a pointless fetch
// attempt on every mount; it doesn't mean offline data is kept any longer
// than CACHE_DURATION_OFFLINE (that's gcTime/maxAge's job, above).
export const STALE_TIME_ONLINE = 1000 * 60 * 60; // 1 hour

/**
 * The gcTime/staleTime/refetchInterval every entity query hook in this app
 * (Issue, Issues, Article, Preview, Collection, per-template Home/Section/
 * Games, Assets, Fonts, ...) should use, centralized so the online/offline
 * policy above is enforced consistently instead of ~22 files each
 * hand-rolling their own `cacheTime` value and `isOnline ? cacheTime :
 * Infinity` pattern (with the *values* drifting between 1 and 60 minutes
 * per file, and every one of them using Infinity -- not 24h -- for offline
 * gcTime, silently overriding defaultQueryClient's connectivity-aware
 * defaults above since per-query options always win over client defaults).
 *
 * ttlMinutes: publisher-configurable per-entity override (the pre-existing
 * `config.app.cache.ttl.<entity>` values -- getIssue, getAllIssues,
 * getFeatureArticles, styles, previewIssue) -- callers that have one pass
 * it through; it replaces CACHE_DURATION_ONLINE/STALE_TIME_ONLINE for that
 * entity's *online* gcTime/staleTime/refetchInterval specifically. Offline
 * behavior (staleTime: Infinity, gcTime: 24h) is intentionally NOT
 * overridable here -- that split is about connectivity, not per-entity
 * freshness, so it stays uniform regardless of which entity is asking.
 *
 * isAdmin bypasses caching entirely (staleTime/gcTime: 0) -- preserved from
 * the pre-existing per-hook pattern, unrelated to the online/offline split;
 * admins are expected to always see live, uncached data.
 */
export function getQueryCacheOptions(args: {
	isOnline: boolean;
	isAdmin?: boolean;
	ttlMinutes?: number;
}): {
	staleTime: number;
	gcTime: number;
	refetchInterval: number | false;
} {
	const { isOnline, isAdmin = false, ttlMinutes } = args;
	if (isAdmin) {
		return { staleTime: 0, gcTime: 0, refetchInterval: false };
	}
	const onlineDuration =
		ttlMinutes !== undefined ? ttlMinutes * 1000 * 60 : CACHE_DURATION_ONLINE;
	const onlineStaleTime =
		ttlMinutes !== undefined ? ttlMinutes * 1000 * 60 : STALE_TIME_ONLINE;
	return {
		staleTime: isOnline ? onlineStaleTime : Infinity,
		gcTime: isOnline ? onlineDuration : CACHE_DURATION_OFFLINE,
		refetchInterval: isOnline ? onlineStaleTime : false,
	};
}

// TanStack Query's own fine-grained persister: one IndexedDB entry per
// query (key = `${QUERY_CACHE_PREFIX}-${queryHash}`), instead of the old
// approach of serializing the whole cache into a single field under
// LEGACY_QUERY_CACHE_KEY. Set as the default `persister` so every query
// benefits without per-hook changes: on first run (no in-memory data yet)
// it transparently restores that query's own entry from storage instead
// of hitting the network, and persists the result after every successful
// fetch.
const QUERY_CACHE_PREFIX = 'canvasflow-query-cache';

function createQueryPersister(maxAge: number) {
	return experimental_createQueryPersister({
		storage,
		prefix: QUERY_CACHE_PREFIX,
		maxAge,
		// Ties persisted-cache invalidation to the build version instead of
		// nuking localStorage wholesale on every deploy (see useBoot() in
		// App.tsx, which still does that for auth/session state -- a
		// separate, broader concern this doesn't touch). A stored entry
		// whose buster doesn't match the running version is treated as a
		// miss and refetched, so a deploy busts the query cache specifically
		// without logging the user out or discarding unrelated localStorage
		// state.
		buster: config.build.version,
		// idb-keyval stores structured-cloneable values directly -- no need
		// to (de)serialize to/from a string the way localStorage would
		// require.
		serialize: (persistedQuery: PersistedQuery) => persistedQuery,
		deserialize: (cached: PersistedQuery) => cached,
	});
}

// experimental_createQueryPersister is stateless -- its returned functions
// just close over their config args and the shared `storage` reference, no
// internal timers/listeners/queues of its own -- so recreating it when
// connectivity changes (below) is safe and doesn't lose or duplicate any
// persisted data; it only changes what maxAge future restore/gc calls use.
// `let`, not `const`, for exactly that reason.
export let queryPersister = createQueryPersister(CACHE_DURATION_OFFLINE);

/**
 * One-time boot step. Drops the old single-blob cache entry left over from
 * the previous persister if a returning user still has one, seeds the
 * in-memory query cache from the new per-query IndexedDB entries so
 * persisted data is available immediately (not just lazily on each query's
 * first mount), and opportunistically clears any per-query entries that
 * have expired.
 */
export async function bootQueryPersistence(
	queryClient: QueryClient,
): Promise<void> {
	await storage.removeItem(LEGACY_QUERY_CACHE_KEY);
	await queryPersister.restoreQueries(queryClient);
	await queryPersister.persisterGc();
}

/**
 * Applies the online/offline cache-duration split above to both
 * defaultQueryClient's gcTime (in-memory) and the persister's maxAge
 * (IndexedDB) -- kept in lockstep since the product decision behind them is
 * the same one. TanStack Query has no networkMode-equivalent for either
 * setting (no built-in way to make them connectivity-aware), so this needs
 * an explicit call -- wire it to the app's connectivity signal (e.g. via
 * useIsOnline()) once, at the App root, *before* bootQueryPersistence() so
 * the first restore/gc pass already uses the right maxAge. Recreating the
 * persister and repointing defaultQueryClient's `persister` default at it
 * doesn't touch React state or force a re-render -- same as the gcTime
 * change, queries just pick up the new defaults the next time they render
 * or are (re)observed.
 */
export function setCacheDurationsForConnectivity(isOnline: boolean): void {
	const duration = isOnline ? CACHE_DURATION_ONLINE : CACHE_DURATION_OFFLINE;
	queryPersister = createQueryPersister(duration);
	defaultQueryClient.setDefaultOptions({
		queries: {
			...defaultQueryClient.getDefaultOptions().queries,
			gcTime: duration,
			persister: queryPersister.persisterFn,
		},
	});
}

export const defaultQueryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Safe initial default before the first
			// setCacheDurationsForConnectivity() call (App.tsx calls it as
			// soon as useIsOnline() resolves, which is immediate -- it's
			// seeded from navigator.onLine, never null -- but this is what's
			// in effect for the handful of queries that could theoretically
			// render before that first effect fires). Matches the initial
			// queryPersister's maxAge above (both default to the offline
			// duration) on purpose: if gcTime were shorter than maxAge, a
			// query could be evicted from the in-memory cache -- and
			// therefore stop being persisted going forward -- before the
			// persisted copy's own maxAge would have expired it, an
			// inconsistency the persister's docs call out explicitly. Was a
			// flat 2 seconds before any of this, which evicted almost
			// immediately after unmount and defeated persistence entirely.
			gcTime: CACHE_DURATION_OFFLINE,
			// Default baseline for any query that doesn't set its own
			// per-entity staleTime (config.app.cache.ttl-driven, most view
			// hooks already do). 1 hour, not seconds: this only controls when
			// an *already-rendered* query is eligible for a background
			// refetch on remount/refocus -- it doesn't block a publisher fix
			// from ever arriving, and doesn't affect first-load or explicit
			// invalidation. Was 10s.
			staleTime: 1000 * 60 * 60,
			// Static on purpose -- previously flipped between 'online' and
			// 'offlineFirst' by an effect in App.tsx keyed off useIsOnline(),
			// which just re-set the same client reference on every connectivity
			// change (see that effect's removal for why). 'offlineFirst' is the
			// right default for an installed offline reader per TanStack
			// Query's own networkMode docs: unlike 'online' (which pauses a
			// query outright while offline, never even attempting it),
			// 'offlineFirst' still attempts the fetch once and falls back to
			// whatever's cached/persisted on failure -- appropriate here since
			// the fetcher (GraphQL POST) has no service-worker-level fallback
			// of its own (topic 2's routes only cover images/fonts/audio/
			// video/styles, not the GraphQL endpoint) to make 'always' safe.
			// Individual hooks that need offline to be an instant, guaranteed
			// no-fetch-attempt (staleTime: isOnline ? cacheTime : Infinity)
			// already layer that on top per-query; this is just the baseline
			// for anything that doesn't.
			networkMode: 'offlineFirst',
			persister: queryPersister.persisterFn,
		},
	},
});

function idbStorage(idbStore: UseStore): AsyncStorage<PersistedQuery> {
	return {
		getItem: async (key) => get(key, idbStore),
		setItem: async (key, value) => set(key, value, idbStore),
		removeItem: async (key) => del(key, idbStore),
		entries: async () => entries(idbStore),
	};
}
